import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * GDPR Consent Categories
 * - necessary: Essential cookies for the website to function (always required)
 * - analytics: Cookies used for analytics and performance tracking
 * - marketing: Cookies used for marketing and advertising purposes
 * - preferences: Cookies to remember user preferences
 */
export interface ConsentPreferences {
    necessary: boolean; // Always true, cannot be disabled
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
}

export interface GDPRState {
    // Whether the user has made a consent decision
    hasConsented: boolean;

    // The timestamp when consent was given
    consentTimestamp: number | null;

    // Individual consent preferences
    consentPreferences: ConsentPreferences;

    // Whether the banner is currently visible
    isBannerVisible: boolean;

    // Whether the preferences modal is open
    isPreferencesModalOpen: boolean;

    // Actions
    acceptAll: () => void;
    rejectAll: () => void;
    savePreferences: (preferences: Partial<ConsentPreferences>) => void;
    showBanner: () => void;
    hideBanner: () => void;
    openPreferencesModal: () => void;
    closePreferencesModal: () => void;
    resetConsent: () => void;
}

const DEFAULT_PREFERENCES: ConsentPreferences = {
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
};

export const useGDPRStore = create<GDPRState>()(
    persist(
        (set, get) => ({
            hasConsented: false,
            consentTimestamp: null,
            consentPreferences: { ...DEFAULT_PREFERENCES },
            isBannerVisible: false,
            isPreferencesModalOpen: false,

            acceptAll: () => {
                const timestamp = Date.now();
                set({
                    hasConsented: true,
                    consentTimestamp: timestamp,
                    consentPreferences: {
                        necessary: true,
                        analytics: true,
                        marketing: true,
                        preferences: true,
                    },
                    isBannerVisible: false,
                    isPreferencesModalOpen: false,
                });

                // Dispatch event for external integrations
                window.dispatchEvent(new CustomEvent("gdpr-consent-update", {
                    detail: {
                        type: "accept-all",
                        preferences: get().consentPreferences,
                        timestamp,
                    }
                }));
            },

            rejectAll: () => {
                const timestamp = Date.now();
                set({
                    hasConsented: true,
                    consentTimestamp: timestamp,
                    consentPreferences: {
                        necessary: true, // Always enabled
                        analytics: false,
                        marketing: false,
                        preferences: false,
                    },
                    isBannerVisible: false,
                    isPreferencesModalOpen: false,
                });

                // Dispatch event for external integrations
                window.dispatchEvent(new CustomEvent("gdpr-consent-update", {
                    detail: {
                        type: "reject-all",
                        preferences: get().consentPreferences,
                        timestamp,
                    }
                }));
            },

            savePreferences: (preferences) => {
                const timestamp = Date.now();
                const newPreferences = {
                    ...get().consentPreferences,
                    ...preferences,
                    necessary: true, // Always ensure necessary is true
                };

                set({
                    hasConsented: true,
                    consentTimestamp: timestamp,
                    consentPreferences: newPreferences,
                    isBannerVisible: false,
                    isPreferencesModalOpen: false,
                });

                // Dispatch event for external integrations
                window.dispatchEvent(new CustomEvent("gdpr-consent-update", {
                    detail: {
                        type: "custom",
                        preferences: newPreferences,
                        timestamp,
                    }
                }));
            },

            showBanner: () => {
                set({ isBannerVisible: true });
            },

            hideBanner: () => {
                set({ isBannerVisible: false });
            },

            openPreferencesModal: () => {
                set({ isPreferencesModalOpen: true });
            },

            closePreferencesModal: () => {
                set({ isPreferencesModalOpen: false });
            },

            resetConsent: () => {
                set({
                    hasConsented: false,
                    consentTimestamp: null,
                    consentPreferences: { ...DEFAULT_PREFERENCES },
                    isBannerVisible: true,
                    isPreferencesModalOpen: false,
                });

                // Dispatch event for external integrations
                window.dispatchEvent(new CustomEvent("gdpr-consent-update", {
                    detail: {
                        type: "reset",
                        preferences: DEFAULT_PREFERENCES,
                        timestamp: null,
                    }
                }));
            },
        }),
        {
            name: "dashbal-gdpr-consent",
            version: 1,
            // Only persist the consent-related fields, not UI state
            partialize: (state) => ({
                hasConsented: state.hasConsented,
                consentTimestamp: state.consentTimestamp,
                consentPreferences: state.consentPreferences,
            }),
        }
    )
);

// Utility hook for checking consent
export const useHasConsent = (category: keyof ConsentPreferences): boolean => {
    return useGDPRStore((state) => state.consentPreferences[category]);
};

// Utility function for non-React contexts
export const hasConsent = (category: keyof ConsentPreferences): boolean => {
    const state = useGDPRStore.getState();
    return state.hasConsented && state.consentPreferences[category];
};
